/**
 * **The Odżywianie tab's vocabulary** — nutrition stage 1 (`nutrition-requirements.md`, ADR
 * 2026-10-08). The shapes `api/clientDetailSource` maps the wire onto, and the few words every
 * projection of the tab shares.
 *
 * The server judges every day (R9) and every week (R10), per nutrient, and this tab only renders
 * the verdicts: nothing here compares a total against a target.
 */

import { dateTimeFormat, numberFormat } from '~/shared/lib/intlCache';
import { parseIsoDate } from '~/shared/lib/calendarDate';

export type NutritionStatus = 'onTarget' | 'offTarget' | 'notLogged' | 'inProgress' | 'noTarget';

/**
 * The server's verdict on one nutrient of one day or week, in the mockup's words: `ok` on target,
 * `warn` off it, `today` still in progress, `plain` no target for it, `none` nothing logged, `na`
 * logged but this nutrient was never given.
 */
export type NutritionVerdict = 'ok' | 'warn' | 'today' | 'plain' | 'none' | 'na';

/** The four judged nutrients, in the order every surface lists them. Sugar is never judged. */
export const NUTRIENTS = ['energy', 'protein', 'carbs', 'fat'] as const;
export type Nutrient = (typeof NUTRIENTS)[number];

export type NutritionVerdicts = Readonly<Record<Nutrient, NutritionVerdict>>;

export interface NutritionAmounts {
  readonly energy: number | null;
  readonly protein: number | null;
  readonly carbs: number | null;
  readonly fat: number | null;
  readonly sugar: number | null;
}

export interface NutritionTargetValues {
  /** `YYYY-MM-DD`, the client's local day the version governs from. */
  readonly effectiveFrom: string;
  readonly energy: number;
  readonly protein: number | null;
  readonly carbs: number | null;
  readonly fat: number | null;
}

export interface NutritionMiss {
  readonly nutrient: Nutrient;
  /** Signed: what was eaten minus the full target; protein's 90% applies only to whether it was met (R9). */
  readonly difference: number;
}

/** A nutrient whose day total can be partial: energy is on every entry (R5), so it never is. */
export type PartialNutrient = Exclude<Nutrient, 'energy'> | 'sugar';

export interface NutritionDay {
  readonly date: string;
  readonly isToday: boolean;
  readonly target: NutritionTargetValues | null;
  readonly totals: NutritionAmounts;
  /** Per nutrient, whether some entry left it out, so its total is a lower bound (R8). */
  readonly partial: Readonly<Record<PartialNutrient, boolean>>;
  readonly status: NutritionStatus;
  readonly reasons: readonly NutritionMiss[];
  readonly verdicts: NutritionVerdicts;
  readonly weightKg: number | null;
  readonly mealCount: number;
}

export interface NutritionSummary {
  readonly loggedDays: number;
  readonly judgedDays: number;
  readonly onTargetDays: number;
  readonly averages: NutritionAmounts;
}

export interface NutritionWeek {
  readonly weekStart: string;
  readonly weekEnd: string;
  readonly summary: NutritionSummary;
  /** Averages over every logged day, today counted: what the week's bar draws. Differs only for the current week. */
  readonly averagesWithToday: NutritionAmounts;
  /** The target governing the week (its newest day's), `null` with none. */
  readonly target: NutritionTargetValues | null;
  readonly verdicts: NutritionVerdicts;
  readonly weightKg: number | null;
}

export interface NutritionRange {
  readonly from: string;
  readonly to: string;
  /** Oldest first, every day of the range. */
  readonly days: readonly NutritionDay[];
  readonly summary: NutritionSummary;
  /** Monday-to-Sunday weeks, oldest first — the 12-week span only. */
  readonly weeks: readonly NutritionWeek[] | null;
  /** ISO instant of the client's newest entry, any day. */
  readonly lastEntryAt: string | null;
}

/**
 * One thing eaten — a scanned product or a hand-typed item — filed in one of the client's meals, or a
 * day total („Cały dzień"), which sits in no meal. `other` is a kind this build does not know yet: it
 * is listed by its name and values only.
 */
export interface NutritionEntry {
  readonly key: string;
  readonly kind: 'product' | 'manual' | 'dayTotal' | 'other';
  /** A product's snapshot name, a manual row's own; null when the client typed none. */
  readonly name: string | null;
  readonly amounts: NutritionAmounts;
  readonly product: NutritionEntryProduct | null;
}

/**
 * One meal the client composed: its stored name (`null` reads as „Posiłek N" by position), the quiet
 * `HH:mm` it was started at, its items in write order and the server's sums of them.
 */
export interface NutritionMeal {
  readonly key: string;
  readonly name: string | null;
  readonly startedAt: string | null;
  readonly items: readonly NutritionEntry[];
  readonly amounts: NutritionAmounts;
}

/** One day read for the week table: the meals in the server's order, then the day total. */
export interface NutritionDayMeals {
  readonly meals: readonly NutritionMeal[];
  readonly dayTotal: NutritionEntry | null;
}

/** A scanned product's snapshot: what the trainer reads beside the name, and where the data came from. */
export interface NutritionEntryProduct {
  readonly brand: string | null;
  readonly grams: number | null;
  readonly sourceUrl: string;
}

/** The three ranges the web offers, keyed by the contract's own `span` words (R10). */
export const NUTRITION_SPANS = ['week', 'fourWeeks', 'quarter'] as const;
export type NutritionSpan = (typeof NUTRITION_SPANS)[number];

export const EMPTY_AMOUNTS: NutritionAmounts = {
  energy: null,
  protein: null,
  carbs: null,
  fat: null,
  sugar: null,
};

export function isPartial(day: NutritionDay, nutrient: Nutrient | 'sugar'): boolean {
  return nutrient !== 'energy' && day.partial[nutrient];
}

/** A total some entry did not give a value to is printed as a floor, „≥ 92". */
export function atLeast(figure: string, partial: boolean): string {
  return partial ? `\u2265\u00a0${figure}` : figure;
}

/** A day with any entry. Energy is required on every entry (R5), so a logged day always has it. */
export function isLogged(day: NutritionDay): boolean {
  return day.totals.energy !== null;
}

/** The status a day is told as: with no target and nothing logged, it reads as not logged. */
export function toldStatus(day: NutritionDay): NutritionStatus {
  return day.status === 'noTarget' && !isLogged(day) ? 'notLogged' : day.status;
}

/** One week of the 12-week read, with the days that fall in it. */
export interface WeekBundle {
  readonly week: NutritionWeek;
  readonly days: readonly NutritionDay[];
  /** The week holding the client's today. */
  readonly current: boolean;
}

/** The 12-week read's weeks, newest first, each with its own days. */
export function weekBundles(range: NutritionRange): WeekBundle[] {
  return [...(range.weeks ?? [])].reverse().map((week) => {
    const days = range.days.filter((day) => day.date >= week.weekStart && day.date <= week.weekEnd);
    return { week, days, current: days.some((day) => day.isToday) };
  });
}

/**
 * The server's margins (R9, backend `DiaryDay.IsMet`), quoted in the words only: energy, carbs and
 * fat are met within ±10% of the target, protein at 90% of it or more. Sugar is never judged.
 */
export type NutritionTargetRule =
  | { readonly kind: 'band'; readonly percent: number }
  | { readonly kind: 'floor'; readonly percent: number };

export const TARGET_BAND_PERCENT = 10;
export const PROTEIN_FLOOR_PERCENT = 90;

export function targetRuleOf(nutrient: Nutrient): NutritionTargetRule {
  return nutrient === 'protein'
    ? { kind: 'floor', percent: PROTEIN_FLOOR_PERCENT }
    : { kind: 'band', percent: TARGET_BAND_PERCENT };
}

export function targetOf(target: NutritionTargetValues | null, nutrient: Nutrient): number | null {
  if (target === null) return null;
  return nutrient === 'energy' ? target.energy : target[nutrient];
}

/** The unit word a nutrient is printed with. */
export function unitOf(nutrient: Nutrient | 'sugar'): 'kcal' | 'g' {
  return nutrient === 'energy' ? 'kcal' : 'g';
}

/** Whole numbers grouped from four digits, „2 196" — Polish leaves „2196" ungrouped otherwise. */
export function wholeNumber(value: number, locale: string): string {
  return numberFormat(locale, { maximumFractionDigits: 0, useGrouping: 'always' }).format(value);
}

/** One decimal: a body weight „63,4", meals a day „3,8", a per-kg rate „2,0". */
export function oneDecimal(value: number, locale: string): string {
  return numberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
}

/** A signed whole number with a typographic minus, „−320" / „+5" / „0". */
export function signedWhole(value: number, locale: string): string {
  const sign = value < 0 ? '−' : value > 0 ? '+' : '';
  return `${sign}${wholeNumber(Math.abs(value), locale)}`;
}

/** A whole amount with its unit, „2 196 kcal". */
export function withUnit(value: number, unit: 'kcal' | 'g', locale: string): string {
  return `${wholeNumber(value, locale)}\u00a0${unit}`;
}

/** A civil date as „5 paź". */
export function dayMonth(date: string, locale: string): string {
  const parsed = parseIsoDate(date);
  return parsed === null ? date : dateTimeFormat(locale, { day: 'numeric', month: 'short' }).format(parsed);
}

/** A week's span as „5–11 paź", or „28 wrz–4 paź" across a month. */
export function weekSpanLabel(start: string, end: string, locale: string): string {
  const from = parseIsoDate(start);
  const to = parseIsoDate(end);
  if (from === null || to === null) return `${start}–${end}`;
  if (start === end) return dayMonth(end, locale);
  const sameMonth = from.getMonth() === to.getMonth();
  const head = sameMonth ? String(from.getDate()) : dayMonth(start, locale);
  return `${head}–${dayMonth(end, locale)}`;
}
